import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnDestroy, Optional } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import { ApiContextDirective } from '../../directives/api-context.directive';
import { ApiService } from '../../services/api.service';
import { ApiCatalogOperation, ApiCatalogService } from '../../services/api-catalog.service';
import { DeveloperModeService } from '../../services/developer-mode.service';
import { buildPayloadExample, getControlPath } from '../../shared/api-field-path';

const EXAMPLE_VALUE = '<valor>';
const COPIED_FEEDBACK_MS = 2000;

/**
 * Icono que, en modo desarrollador, aparece junto al label de un campo de formulario y muestra como manipular ese
 * mismo dato desde la API publica: metodo, URL y el payload minimo con la ruta del campo dentro del cuerpo.
 *
 * Esta embebido en los componentes de input compartidos (app-input, app-select, ...), asi que no hay que anadirlo
 * campo a campo: basta con declarar el endpoint del formulario con [appApiContext].
 */
@Component({
  selector: 'app-api-field-helper',
  templateUrl: './api-field-helper.component.html',
  styleUrls: ['./api-field-helper.component.scss'],
})
export class ApiFieldHelperComponent implements OnDestroy {
  /** Control del que se deriva la ruta del campo en el payload. */
  @Input() control: AbstractControl | null | undefined;

  /**
   * Ruta del campo dentro del payload, para los formularios que transforman el valor antes de enviarlo y por tanto
   * no coinciden con el nombre del FormControl (p.ej. 'associations.issuer.id').
   * Con `null` se oculta el helper: el campo no viaja a la API (confirmacion de contrasena, aceptacion de terminos).
   */
  @Input() apiField: string | null | undefined;

  copied: boolean = false;

  private copiedTimer: number | undefined;

  constructor(
    @Optional() private context: ApiContextDirective | null,
    private developerMode: DeveloperModeService,
    private apiCatalog: ApiCatalogService,
    private clipboard: Clipboard,
    private api: ApiService,
  ) {}

  /**
   * El helper solo se pinta para endpoints que aparecen en el catalogo que sirve el back (GET /api-reference), que
   * solo incluye operaciones publicas. Un endpoint privado funciona igual, pero no es contrato publico: ofrecerlo
   * como referencia de integracion acoplaria al cliente a algo que puede cambiar sin aviso. La comprobacion vive
   * aqui y no en quien anota para que la regla no dependa de recordarla en cada formulario.
   */
  get show(): boolean {
    return this.developerMode.enabled
      && !!this.catalogEntry
      && this.apiField !== null
      && !!this.fieldPath.length;
  }

  /** Operacion del catalogo del back que corresponde a la declarada por el formulario con [appApiContext]. */
  get catalogEntry(): ApiCatalogOperation | null {
    const path = this.context?.operationPath;
    return path ? this.apiCatalog.find(path) : null;
  }

  /** Ruta del campo en el payload: la anotada a mano si la hay, y si no la posicion del control en el formulario. */
  get fieldPath(): string[] {
    if (this.apiField) return this.apiField.split('.');
    return this.control ? getControlPath(this.control) : [];
  }

  /** Ruta completa dentro del cuerpo de la peticion, incluyendo el envoltorio que declare el formulario (apiBodyRoot). */
  get bodyPath(): string[] {
    const root = this.context?.apiBodyRoot;
    return root ? [root, ...this.fieldPath] : this.fieldPath;
  }

  get fieldPathLabel(): string {
    return this.bodyPath.join('.');
  }

  /** URL absoluta del recurso, con los parametros de ruta sustituidos por los del formulario cuando se conocen. */
  get url(): string {
    const entry = this.catalogEntry;
    if (!entry) return '';

    const params = this.context?.apiPathParams ?? {};
    const path = entry.path.replace(/{(\w+)}/g, (match, key: string) => params[key] || match);
    return this.api.endpoint + path;
  }

  /**
   * Cuerpo minimo de la peticion. El back espera los recursos envueltos en `data` (ver ApiService.formatData);
   * los endpoints multipart mandan ese mismo JSON serializado en el campo `data` de un FormData junto al fichero.
   */
  get payload(): string {
    return JSON.stringify({ data: buildPayloadExample(this.bodyPath, EXAMPLE_VALUE) }, null, 2);
  }

  get snippet(): string {
    const entry = this.catalogEntry;
    if (!entry) return '';

    return `${entry.method} ${this.url}\n\n${this.payload}`;
  }

  copy(event: MouseEvent): void {
    event.stopPropagation();
    this.clipboard.copy(this.snippet);
    this.copied = true;
    // El helper vive dentro de modales que pueden cerrarse antes de que expire el aviso de copiado.
    window.clearTimeout(this.copiedTimer);
    this.copiedTimer = window.setTimeout(() => this.copied = false, COPIED_FEEDBACK_MS);
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.copiedTimer);
  }
}
